import { useEffect, useState } from 'react';

import { FormProvider, useForm, useWatch } from 'react-hook-form';
import TickIcon from 'assets/icons/tick.svg';
import WarningIcon from 'assets/icons/warning.svg';
import ProgressIcon from 'assets/icons/progress.svg';
import DeleteIcon from 'assets/icons/delete.svg';
import RefreshIcon from 'assets/icons/refresh.svg';
import EditIcon from 'assets/icons/edit.svg';
import Pill, { PillModifier } from './pill';
import FormCheckbox, { FormCheckboxProps } from './form/checkbox';
import Btn from './btn';

export enum ApiCardStatusEnum {
  PROGRESS = 'progress',
  ACTIVE = 'active',
  ERROR = 'error',
}

export interface ApiCardStatus {
  type: ApiCardStatusEnum;
  message: string | null;
}

export interface ApiCardDetail {
  key: string;
  value: string | boolean | number | null;
}

export interface ApiCardData {
  logoUrl: string;
  key: string;
  autosync: boolean;
  customName: string | null;
  details: ApiCardDetail[];
  status: ApiCardStatus;
}

export interface ApiCardProps {
  data: ApiCardData;
}

function ApiCard(props: ApiCardProps) {
  const { ...data } = props.data;
  const formMethods = useForm({ mode: 'onChange' });
  const [status, setStatus] = useState<ApiCardStatus>({ ...data.status });

  const toggleData: FormCheckboxProps = {
    name: data.key,
    label: 'Automatic sync',
    toggle: true,
    defaultChecked: data.autosync,
  };

  const toggleValue: unknown = useWatch({
    control: formMethods.control,
    name: data.key,
    exact: true,
  });

  useEffect(() => {
    if (toggleValue !== undefined) {
      // eslint-disable-next-line no-console
      console.log('handle toggle. Value:', toggleValue);
    }
  }, [toggleValue]);

  const handleDelete = () => {
    // eslint-disable-next-line no-console
    console.log('handle delete');
  };

  const handleSync = () => {
    // eslint-disable-next-line no-console
    console.log('handle sync');
    setStatus({ ...status, type: ApiCardStatusEnum.PROGRESS });
  };

  const handleEdit = () => {
    // eslint-disable-next-line no-console
    console.log('handle edit');
  };

  return (
    <div className="ct_apiCard">
      <div className="ct_apiCard-header">
        <div className="ct_apiCard-image">
          <img src={data.logoUrl} alt="" />
        </div>
        <div className="ct_apiCard-infos">
          <div className="ct_apiCard-info">
            <span>Key</span>
            <span>{data.key}</span>
          </div>
          <div className="ct_apiCard-info">
            <span>Secret</span>
            <span>********************************************</span>
          </div>
          <div className="ct_apiCard-info">
            <span>Custom name</span>
            <span>{data.customName || '-'}</span>
          </div>
        </div>
        <div className="ct_apiCard-action">
          <FormProvider {...formMethods}>
            <form>
              <FormCheckbox {...toggleData} />
            </form>
          </FormProvider>
        </div>
      </div>
      <div className="ct_apiCard-body">
        <div className="ct_apiCard-details">
          {data.details.map((detail, index) => (
            <div key={index} className="ct_apiCard-detail">
              <span>{detail.key}</span>
              <span>{detail.value || '-'}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="ct_apiCard-footer">
        <div className="ct_apiCard-status">
          {status.type === ApiCardStatusEnum.PROGRESS && (
            <Pill value="Import in progress" modifiers={[PillModifier.PROGRESS]} icon={<ProgressIcon />} />
          )}
          {status.type === ApiCardStatusEnum.ACTIVE && (
            <Pill value="Active" modifiers={[PillModifier.ACTIVE]} icon={<TickIcon />} />
          )}
          {status.type === ApiCardStatusEnum.ERROR && (
            <Pill value="Api issue" modifiers={[PillModifier.ERROR]} icon={<WarningIcon />} />
          )}
        </div>
        <div className="ct_apiCard-actions">
          <Btn onClick={handleDelete} type="link" text="Delete" icon={<DeleteIcon />} />
          <Btn onClick={handleSync} type="link" text="Sync" icon={<RefreshIcon />} />
          <Btn onClick={handleEdit} type="link" text="Edit" icon={<EditIcon />} />
        </div>
      </div>
      {status.type === ApiCardStatusEnum.ERROR && status.message && (
        <div className="ct_apiCard-errors">
          <div className="ct_apiCard-error">{status.message}</div>
          {/* {data.errors.map((error, index) =>
            <div key={index} className="ct_apiCard-error">{error}</div>)} */}
        </div>
      )}
    </div>
  );
}

export default ApiCard;
